/**
 * DEC-584 + DEC-55: Beta access context.
 *
 * Server-validated gate flow:
 *
 *   1. On mount, auto-redeem if `?invite=<token>` is in the URL → server
 *      sets the httpOnly `deco_beta` cookie and returns granted=true.
 *   2. Then call POST /api/beta-access/check — granted via cookie or
 *      via an accepted invitation tied to the authenticated user's email.
 *   3. Cache the granted state in localStorage so a positive answer
 *      survives page reloads without an extra round-trip; the server
 *      remains the source of truth (re-checked on next mount).
 *
 * DEC-55 retired the legacy password path. The gate page is now a
 * waitlist email capture (BetaAccessGate.js) and never calls /check
 * with credentials. The server still honors BETA_ALLOW_LEGACY_PASSWORD
 * if any out-of-band caller hits it, but the client never sends the
 * legacy header anymore.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { redeemErrorFromStatus } from './betaRedeemError';

const BetaAccessContext = createContext();

const API_URL = process.env.REACT_APP_API_URL || '/api';
const STORAGE_KEY = 'betaAccess';

// Lightweight axios instance — withCredentials so the deco_beta cookie
// rides along on every /check call.
const betaApi = axios.create({ baseURL: API_URL, withCredentials: true });

// DEC-721 follow-up: extract BOTH the invite token (?invite=…) and the
// optional email hint (#e=…) from the URL on mount. The email hint is
// base64url-encoded and lives in the URL fragment — fragments are NEVER
// sent to servers, so the email doesn't show up in CDN/proxy/access logs
// (closing the leak that drove DEC-603).
//
// The email is purely a UX convenience for pre-filling the AuthModal on
// "Create your account" — it carries no authentication weight, since
// registration still requires an OTP code to that email.
//
// MODULE-LEVEL CACHE: React 18 StrictMode double-invokes useEffect in
// development — first mount runs effect, immediately unmounts (cleanup
// fires), then remounts and runs effect again. Without a cache, the
// first mount strips the URL params and the second mount finds nothing
// to redeem. The cache survives across the StrictMode cycle (and across
// any future component re-mounts in the same SPA session) so both mounts
// see the same params. The cached promise also de-dupes the /redeem call.
let cachedInviteParams = null;
let cachedRedemptionPromise = null;

function readInviteParamsFromUrl() {
  if (cachedInviteParams !== null) return cachedInviteParams;
  if (typeof window === 'undefined') {
    cachedInviteParams = { token: null, email: null };
    return cachedInviteParams;
  }
  const params = new URLSearchParams(window.location.search);
  const token = params.get('invite');

  let email = null;
  if (window.location.hash) {
    // Hash looks like "#e=<base64url>" or "#e=<base64url>&other=bar".
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const encoded = hashParams.get('e');
    if (encoded) {
      try {
        // atob doesn't natively understand base64url's `-`/`_`; convert.
        const standardB64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
        // decodeURIComponent + escape handles UTF-8 round-trip from atob's bytes.
        email = decodeURIComponent(escape(atob(standardB64)));
      } catch {
        email = null;
      }
    }
  }

  if (!token && !email) {
    cachedInviteParams = { token: null, email: null };
    return cachedInviteParams;
  }

  // Strip both the ?invite= param AND the #e= fragment so they don't
  // persist on subsequent reloads. We rebuild the URL from scratch.
  params.delete('invite');
  const newSearch = params.toString();
  const newUrl =
    window.location.pathname +
    (newSearch ? `?${newSearch}` : '');
  // Empty hash explicitly omitted — drops #e=… from the visible URL.
  window.history.replaceState({}, '', newUrl);

  cachedInviteParams = { token, email };
  return cachedInviteParams;
}

// De-dupes the /redeem network call across StrictMode double-invocation
// (and any other reason the provider might re-mount with the same params).
// Both mounts await the same promise; the server only sees one /redeem.
//
// DEC-1030: redemption failures are CAPTURED, not swallowed. The old
// `.catch(() => null)` meant an expired invite link (410) rendered the
// plain waitlist gate with zero explanation — the prod user who hit it had
// no idea their link was dead, registered anyway, and got trapped behind
// the gate. Failures resolve to { granted: false, errorStatus } so the
// provider can tell the gate WHY.
async function redeemInvite(token, betaApi) {
  if (!cachedRedemptionPromise) {
    cachedRedemptionPromise = betaApi
      .get(`/beta-access/redeem?t=${encodeURIComponent(token)}`)
      .then(res => res.data)
      .catch(err => ({ granted: false, errorStatus: err?.response?.status ?? null }));
  }
  return cachedRedemptionPromise;
}

// DEC-1030: the status→error-key mapping lives in betaRedeemError.js (pure,
// axios-free) so it stays unit-testable under CRA's jest. Re-exported here
// for callers that reach it via the context module.
export { redeemErrorFromStatus };

// DEC-721: localStorage flag that prevents the welcome modal from
// re-firing on subsequent visits. Set when the user dismisses the modal.
const WELCOMED_KEY = 'deco_beta_welcomed';

export const BetaAccessProvider = ({ children }) => {
  const [hasBetaAccess, setHasBetaAccess] = useState(() => {
    return localStorage.getItem(STORAGE_KEY) === 'granted';
  });
  const [loading, setLoading] = useState(true);

  // DEC-721: welcome-modal context — set ONLY when the user just
  // redeemed an invite token (not on cookie revisits, not on email-match
  // logins). Read by BetaWelcomeModal which then clears the flag and
  // sets `deco_beta_welcomed` so a refresh in the same session doesn't
  // re-fire the modal.
  const [welcomeContext, setWelcomeContext] = useState(null);

  // DEC-1030: why the ?invite= redemption failed, if it did —
  // 'expired' | 'invalid' | 'error' | null. The gate renders an explicit
  // banner from this instead of silently showing the waitlist form.
  const [inviteError, setInviteError] = useState(null);

  // Ref-mirror of hasBetaAccess so verifyAccess can read the latest value
  // in its catch fallback without listing it as a dependency. Listing it
  // would recreate verifyAccess on every grant, re-trigger the mount
  // effect with no credentials, and immediately revoke the grant.
  const hasBetaAccessRef = useRef(hasBetaAccess);
  useEffect(() => { hasBetaAccessRef.current = hasBetaAccess; }, [hasBetaAccess]);

  // Stable across the component's lifetime — no deps means no recreations,
  // which keeps the mount effect from re-firing whenever access changes.
  const verifyAccess = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await betaApi.post('/beta-access/check', {}, { headers });
      const granted = !!res.data?.granted;
      if (granted) {
        localStorage.setItem(STORAGE_KEY, 'granted');
        setHasBetaAccess(true);
      } else {
        // Mount-time check failed cleanly — user has no cookie / no invite.
        // Clear localStorage so a refresh doesn't show a stale "granted".
        localStorage.removeItem(STORAGE_KEY);
        setHasBetaAccess(false);
      }
      return granted;
    } catch (err) {
      // Surface the failure so DevTools shows something actionable.
      // The catch only fires on network errors and 5xx — 4xx returns from
      // the server still have res.data with granted: false.
      // eslint-disable-next-line no-console
      console.error('[beta-access] /check failed:', err?.response?.status, err?.response?.data || err?.message);
      return hasBetaAccessRef.current;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Step 1: if URL has ?invite=, redeem it. Server sets the cookie.
      // The optional #e=<email> hint pre-fills the AuthModal email when
      // the user clicks "Create your account" in the welcome modal.
      //
      // BOTH the param read AND the redeem call are cached at module
      // level so React 18 StrictMode's double-mount doesn't lose the
      // redemption (first mount strips URL → second mount would see
      // nothing). Both mounts await the same promise; the cached `email`
      // survives the URL strip; only one network call is made.
      const { token: inviteToken, email: inviteeEmail } = readInviteParamsFromUrl();
      if (inviteToken) {
        const data = await redeemInvite(inviteToken, betaApi);
        // DEC-1030: a failed redemption is announced, never silent — the
        // recipient must learn their link is dead while they can still ask
        // for a fresh one (or sign in, if they already have an account).
        if (data && !data.granted && !cancelled) {
          setInviteError(redeemErrorFromStatus(data.errorStatus));
        }
        if (data?.granted && !cancelled) {
          localStorage.setItem(STORAGE_KEY, 'granted');
          setHasBetaAccess(true);
          // DEC-721: trigger the welcome modal — only on the redeem
          // path, not the cookie/email-match paths. localStorage flag
          // suppresses re-fire on subsequent visits in the same browser.
          const alreadyWelcomed = localStorage.getItem(WELCOMED_KEY) === '1';
          if (!alreadyWelcomed) {
            setWelcomeContext({
              inviterFirstName: data.inviterFirstName || null,
              inviteeEmail: inviteeEmail || null,
              // DEC-721 follow-up: server pre-mints an ownership token
              // tied to the invitee email so registration can skip OTP
              // verification (clicking the invite link IS the proof).
              ownershipToken: data.ownershipToken || null,
            });
          }
        }
      }
      // Step 2: server-side check (cookie or accepted-invite path).
      await verifyAccess();
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
    // Empty deps: this only runs on mount. verifyAccess is stable; we
    // don't want re-runs to undo a successful password grant.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const revokeBetaAccess = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setHasBetaAccess(false);
  }, []);

  // DEC-721: called by BetaWelcomeModal after the user dismisses. Clears
  // the in-memory flag AND writes the localStorage marker so the modal
  // never re-fires for this browser.
  const dismissWelcome = useCallback(() => {
    try { localStorage.setItem(WELCOMED_KEY, '1'); } catch { /* ignore */ }
    setWelcomeContext(null);
  }, []);

  return (
    <BetaAccessContext.Provider value={{
      hasBetaAccess,
      loading,
      revokeBetaAccess,
      welcomeContext,
      dismissWelcome,
      inviteError,
    }}>
      {children}
    </BetaAccessContext.Provider>
  );
};

export const useBetaAccess = () => {
  const context = useContext(BetaAccessContext);
  if (!context) {
    throw new Error('useBetaAccess must be used within a BetaAccessProvider');
  }
  return context;
};
